import type { Product, ProductApiResponse, ProductImageApiResponse, ProductImage } from '../types';

export const transformImageFromApi = (image: ProductImageApiResponse): ProductImage => ({
  id: image.id,
  url: image.imageUrl || image.url || '',
  sortOrder: image.sortOrder,
  roles: image.roles,
  roleSortOrders: image.roleSortOrders,
});

const transformImageToApi = (image: ProductImage): ProductImageApiResponse => ({
  id: image.id,
  imageUrl: image.url,
  url: image.url,
  sortOrder: image.sortOrder,
  roles: image.roles,
  roleSortOrders: image.roleSortOrders,
});

/**
 * Builds summary string from product title, pack configuration and quantity description
 */
export const buildSummary = (apiProduct: ProductApiResponse): string => {
  const title = apiProduct.title || '';
  const packConfig = apiProduct.multipackType?.trim()
    ? apiProduct.multipackType
    : '{no pack config}';
  const quantityDesc = apiProduct.quantityDescription?.trim()
    ? apiProduct.quantityDescription
    : '{no qty}';

  return `${title} (${packConfig} - ${quantityDesc})`;
};

/**
 * Builds category display string from category and subcategory
 */
export const buildCategoryDisplay = (apiProduct: ProductApiResponse): string => {
  const category = apiProduct.category?.trim() ? apiProduct.category : '{No Category}';
  const subcategory = apiProduct.subCategory?.trim() ? apiProduct.subCategory : '{No Subcat}';

  return `${category}/${subcategory}`;
};

/**
 * Transforms product data from API response format to frontend format
 */
export const transformProductFromApi = (apiProduct: ProductApiResponse): Product => {
  const isVirtual = apiProduct.isVirtual ?? apiProduct.category === 'Virtual';

  return {
    id: apiProduct.id,
    shopifyProductId: apiProduct.shopifyId?.toString() || null,
    shopifyVariantId: apiProduct.productVariantId?.toString() || null,
    title: apiProduct.title || '',
    primaryImageUrl: apiProduct.imageURL || null,
    sku: apiProduct.sku || null,
    actualPrice: apiProduct.price ?? null,
    listPrice: apiProduct.listPrice ?? null,
    isVirtual: isVirtual,
    isActive: apiProduct.isActive ?? true,
    category: apiProduct.category || null,
    subcategory: apiProduct.subCategory || null,
    aboutThisRecipe: apiProduct.aboutThisRecipe ?? null,
    packConfiguration: apiProduct.multipackType || null,
    quantityDescription: apiProduct.quantityDescription || null,
    lowStock: apiProduct.isLowOnStock ?? false,
    swapWithVariantId: apiProduct.swapWithVariantId ?? null,
    allowedSubscriptions: apiProduct.allowedSubscriptions || 'None',
    sortOrder: apiProduct.sortOrder ?? null,
    oneOff: apiProduct.isOneOff ?? false,
    description: apiProduct.description || null,
    shortDescription: apiProduct.shortDescription || null,
    upsellCarouselDescription: apiProduct.upsellCarouselDescription || null,
    composition: apiProduct.composition || null,
    analyticalConstituents: apiProduct.analyticalConstituents || null,
    nutritionalAdditives: apiProduct.nutritionalAdditives || null,
    images: (apiProduct.images || []).map(transformImageFromApi),
    tags: apiProduct.tags ?? [],
    createdAt: apiProduct.createdAt ?? null,
    updatedAt: apiProduct.updatedAt ?? null,
    deletedAt: apiProduct.deletedAt ?? null,
    lastModifiedBy: apiProduct.lastModifiedBy,
    isUsedInSubscription: apiProduct.isUsedInSubscription,
    summary: buildSummary(apiProduct),
    categoryDisplay: buildCategoryDisplay(apiProduct),
    swapStatus: apiProduct.swapStatus || (apiProduct.swapWithVariantId ? 'Active' : 'None'),
    shopifyProductUrl: apiProduct.shopifyProductUrl || null,
    groupedProduct: apiProduct.groupedProduct ?? null,
    varietyPackLines: normalizeVarietyPackLinesFromApi(apiProduct.varietyPackLines),
  };
};

const normalizeVarietyPackLinesFromApi = (
  raw: ProductApiResponse['varietyPackLines'],
): Record<string, number> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isNaN(n) && n >= 0) {
      out[String(k)] = n;
    }
  }
  return out;
};

/**
 * Transforms product data from frontend format to API request format
 */
export const transformProductToApi = (product: Partial<Product>): Partial<ProductApiResponse> => {
  return {
    id: product.id,
    shopifyId: product.shopifyProductId ? Number(product.shopifyProductId) : undefined,
    productVariantId: product.shopifyVariantId ? Number(product.shopifyVariantId) : undefined,
    title: product.title,
    imageURL: product.primaryImageUrl || null,
    sku: product.sku || null,
    price: product.actualPrice ?? null,
    listPrice: product.listPrice ?? null,
    isVirtual: product.isVirtual ?? false,
    isActive: product.isActive ?? true,
    category: product.category || null,
    subCategory: product.subcategory || null,
    aboutThisRecipe: product.aboutThisRecipe || null,
    multipackType: product.packConfiguration || null,
    quantityDescription: product.quantityDescription || null,
    isLowOnStock: product.lowStock ?? false,
    isOneOff: product.oneOff ?? false,
    sortOrder: product.sortOrder ?? null,
    allowedSubscriptions: product.allowedSubscriptions,
    swapWithVariantId: product.swapWithVariantId ?? null,
    description: product.description || null,
    shortDescription: product.shortDescription || null,
    upsellCarouselDescription: product.upsellCarouselDescription || null,
    composition: product.composition || null,
    analyticalConstituents: product.analyticalConstituents || null,
    nutritionalAdditives: product.nutritionalAdditives || null,
    images: (product.images || []).map(transformImageToApi),
    varietyPackLines:
      product.packConfiguration === 'Sleeve (variety)'
        ? (product.varietyPackLines ?? {})
        : undefined,
    tags: product.tags?.map((tag) => tag.trim()).filter((tag) => tag.length > 0) ?? [],
  };
};
